import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { ArrowRight, CheckCircle2, TrendingUp } from 'lucide-react';
import { servicesSeedData } from '../../database/seedData/services';
import EngagementTriggerMap from '../../components/EngagementTriggerMap';
import ExecutiveSummary from '../../components/ExecutiveSummary';
import {
  DataEstateLayersDiagram,
  ExecutiveFlowRail,
  ExecutiveSplitSection,
} from '../../components/executive';

const dataEngagementLanes = (useCases) => [
  {
    label: 'Risk & Oversight',
    description: 'When boards, auditors, or regulators ask hard questions.',
    icon: 'shield',
    triggers: [useCases[0], useCases[4]].filter(Boolean),
  },
  {
    label: 'Architecture & Investment',
    description: 'When platform direction, spend, or overlap lacks clarity.',
    icon: 'layers',
    triggers: [useCases[1], useCases[3], useCases[5]].filter(Boolean),
  },
  {
    label: 'AI & Alignment',
    description: 'When data cannot support the pace of analytics or AI.',
    icon: 'sparkles',
    triggers: [useCases[2], useCases[6]].filter(Boolean),
  },
];

const DataAdvisory = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const service = servicesSeedData.find(s => s.slug === 'data-advisory');

  if (!service) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-24`}>
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className={`text-5xl font-bold mb-8 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Service Not Found</h1>
          <button onClick={() => navigate('/services')} className={`px-10 py-4 rounded-xl text-xl font-semibold ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}>View All Services</button>
        </div>
      </div>
    );
  }

  const phases = service.timeline
    ? [
        { title: 'Orient', description: service.timeline.discovery },
        { title: 'Decide', description: service.timeline.design },
        { title: 'Advise', description: service.timeline.implementation },
      ].filter((entry) => entry.description)
    : [];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
      {/* Hero */}
      <div className={`relative ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'} overflow-hidden`}>
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-cyan-400 via-teal-500 to-cyan-600"></div>

        <div className="max-w-7xl mx-auto px-8 py-24 lg:py-32">
          <div className="flex justify-between items-center mb-12">
            <button
              onClick={() => navigate('/services')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-base font-semibold bg-white/10 text-white border border-white/30 hover:bg-white/20 transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Services
            </button>
            <span className="text-sm uppercase tracking-[0.4em] text-cyan-400/80">
              Shared Oxygen Advisory
            </span>
          </div>
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <div className="h-px w-12 bg-cyan-500 mr-4"></div>
              <span className="text-cyan-400 uppercase tracking-widest text-sm font-semibold">
                Data &amp; Governance
              </span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              {service.title}
            </h1>

            <p className="text-2xl lg:text-3xl text-gray-200 font-normal leading-relaxed mb-12">
              {service.shortDescription}
            </p>

            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center px-12 py-5 bg-cyan-500 hover:bg-cyan-600 text-gray-900 text-lg font-bold rounded-none transition-all duration-300"
            >
              Request a Conversation
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className={`${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-20`}>
        <div className="max-w-6xl mx-auto px-8">
          <ExecutiveSummary theme={theme} variant="cyan">
            {service.description}
          </ExecutiveSummary>
        </div>
      </div>

      {/* Context for leadership */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto px-8">
          <ExecutiveSplitSection
            theme={theme}
            accentBar="bg-cyan-500"
            title="The Leadership Challenge"
            illustration={<DataEstateLayersDiagram theme={theme} />}
          >
            <p>
              Most organizations already spend heavily on data—warehouses, lakes, integration tools, and
              vendor contracts accumulated over years. The gap is rarely technology. It is judgment: which
              assets matter to the business, who owns them, what they cost to maintain, and whether they meet
              the bar for regulated use or production AI.
            </p>
            <p>
              This is advisory work for executives who need a clear-headed view—not another platform roadmap.
              We do not sell warehouses or integration hours. We sit with leadership to test assumptions,
              challenge vendor narratives, and work through the questions boards and regulators are already
              asking.
            </p>
          </ExecutiveSplitSection>
        </div>
      </div>

      {/* Where we focus & What changes */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-cyan-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Where We Focus
                </h3>
                <p className={`mt-3 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  The questions we help leadership think through—not a fixed scope of work.
                </p>
              </div>
              <div className="space-y-6">
                {service.keyFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start group">
                    <CheckCircle2 className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-emerald-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  What Changes
                </h3>
                <p className={`mt-3 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  Shifts in judgment and alignment—not implementation milestones.
                </p>
              </div>
              <div className="space-y-6">
                {service.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start group">
                    <TrendingUp className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* When leaders engage */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-14">
            <div className="h-1 w-20 bg-cyan-500 mb-6"></div>
            <h2 className={`text-4xl lg:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              When Leaders Engage
            </h2>
            <p className={`mt-4 text-lg max-w-3xl ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
              Situations where an independent view helps—often where spend, risk, and an AI agenda have
              moved faster than the underlying data estate.
            </p>
          </div>

          <EngagementTriggerMap
            lanes={dataEngagementLanes(service.useCases)}
            theme={theme}
            variant="cyan"
            hubLabel="Where judgment is needed"
          />
        </div>
      </div>

      {/* How we engage */}
      {phases.length > 0 && (
        <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/40' : 'bg-gray-50'}`}>
          <div className="max-w-6xl mx-auto px-8">
            <div className="mb-12">
              <div className="h-1 w-16 bg-cyan-500 mb-6"></div>
              <h2 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                How We Engage
              </h2>
              <p className={`mt-4 text-lg max-w-3xl ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                We work alongside leadership at the pace the situation requires. There is no standard
                engagement model—depth and duration follow the decisions in front of you.
              </p>
            </div>

            <ExecutiveFlowRail phases={phases} theme={theme} variant="cyan" />
          </div>
        </div>
      )}

      {/* CTA */}
      <div className={`py-24 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'}`}>
        <div className="max-w-5xl mx-auto px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Discuss Your Data Priorities
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            A short conversation to understand your situation. No obligation, no pitch deck.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="px-16 py-6 bg-cyan-500 hover:bg-cyan-600 text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Contact Us
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-16 py-6 border-2 border-white text-white hover:bg-white hover:text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Explore All Services
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataAdvisory;
